
import {
  Action,
  Component,
  Getter,
  Prop,
  State,
  Watch,
  Vue,
} from 'nuxt-property-decorator';
import { Context } from '@nuxt/types';
import { TranslateResult } from 'vue-i18n';
import WatchVideoDialog from '~/components/Sanity/WatchVideoDialog.vue';
import { IGameItemForSaleByGala } from '~/store/games/types';
import { IGamesState, IFetchItemsForSalePayload } from '~/store/games/types';
import { MuxStreamTypes } from '~/types/mux-player';
import { SanityDocumentSchema } from '~/types/sanity-documents';
import VideoPlayer from '../Player/VideoPlayer.vue';
import TakeoverCastMember from './TakeoverCastMember.vue';
import TrailerTakeoverItemContent from './TrailerTakeoverItemContent.vue';

const schema = SanityDocumentSchema.PROJECT;

interface ITrailerTab {
  name: string;
  text: TranslateResult;
  show: Boolean;
}

@Component({
  components: {
    WatchVideoDialog,
    VideoPlayer,
    TakeoverCastMember,
    TrailerTakeoverItemContent,
  },
})
export default class TrailerTakeover extends Vue {
  @Prop(String) readonly projectRef!: string;

  @Action('getItemsForSaleByGala', { namespace: 'games' })
  private getItemsForSaleByGala!: (payload: IFetchItemsForSalePayload) => void;

  @Action('showProjectNftPromotion', { namespace: 'projects' })
  private showProjectNftPromotion!: (projectSlug: string) => void;

  @State((state: IGamesState) => state.itemsForSaleByGala, {
    namespace: 'games',
  })
  readonly itemsForSale!: IGamesState['itemsForSaleByGala'];

  @State((state: IGamesState) => state.itemsForSaleState, {
    namespace: 'games',
  })
  readonly itemsForSaleState!: IGamesState['itemsForSaleState'];

  docType = SanityDocumentSchema.VIDEO;
  muxStreamType = MuxStreamTypes.ON_DEMAND;
  gType = SanityDocumentSchema.GENRE;
  tType = SanityDocumentSchema.TAG;
  person = SanityDocumentSchema.PERSON;

  activeTab: string = 'watch';

  playIcon = require('@/assets/icons/play-icon-no-circle.svg');

  async fetch() {
    await this.$sanityLookup.fetchDocument({
      _type: schema,
      _id: this.projectRef,
      fetchReferences: true,
    });

    await this.$sanityLookup.fetchDocument({
      _type: this.docType,
      _id: this.project?.trailer?._ref,
    });

    await this.getItemsForSaleByGala({
      game: process.env.gameItemProductsGame,
      category: this.projectSlug,
    });
  }

  get tabs() {
    return [
      { name: 'watch', text: this.$t('common.misc.watch'), show: true },
      {
        name: 'unlock',
        text: this.$t('common.misc.unlock'),
        show: this.mysteryBoxProduct !== undefined,
      },
      {
        name: 'own',
        text: this.$t('common.misc.own'),
        show: this.momentPackProduct !== undefined,
      },
    ];
  }

  get pageState() {
    return (
      this.$sanityLookup.getDocumentFromId({
        _type: schema,
        _id: this.projectRef,
      }) || {
        isLoading: false,
        hasError: false,
      }
    );
  }

  get trailerDoc() {
    if (!this.trailerId) {
      return;
    }

    return this.$sanityLookup.getDocumentFromId({
      _id: this.trailerId,
      _type: this.docType,
    })?.data;
  }

  get project() {
    return this.pageState?.data;
  }

  get isLoading() {
    return this.pageState?.isLoading;
  }

  get productsLoading() {
    return this.itemsForSaleState.isLoading;
  }

  get mysteryBoxProduct() {
    const regex = /\bMystery Box\b/i;
    return this.itemsForSale?.find((item: IGameItemForSaleByGala) =>
      regex.test(item.name),
    );
  }

  get momentPackProduct() {
    const regex = /\bMoment Pack\b/i;
    return this.itemsForSale?.find((item: IGameItemForSaleByGala) =>
      regex.test(item.name),
    );
  }

  get hasError() {
    return this.pageState?.hasError;
  }

  get posterImageUrl() {
    if (!this.project?.pageHeroImage) {
      return undefined;
    }

    return this.project?.pageHeroImage;
  }

  get computedStyles() {
    return {
      backgroundImage:
        !this.trailerDoc || this.$vuetify.breakpoint.mdAndDown
          ? `url(${this.bgDefaultImage})`
          : 'none',
    };
  }

  get bgDefaultImage() {
    if (!this.project?.pageHeroImage) {
      return undefined;
    }

    const w = 960;
    const h = Math.round(w * (9 / 16));
    return this.$sanityImage
      .urlFor(this.project?.pageHeroImage)
      .width(w)
      .height(h)
      .fit('crop')
      .url();
  }

  get trailerId() {
    return this.project?.trailer?._ref;
  }

  get projectSlug() {
    return this.project?.slug;
  }

  get logo() {
    if (!this.project?.logo) {
      return;
    }

    return this.$sanityImage.urlFor(this.project.logo).url();
  }

  get title() {
    return this.project?.title || '';
  }

  get genres() {
    return this.project?.genres ?? [];
  }

  get tags() {
    return this.project?.overviewTags ?? [];
  }

  get releaseDate() {
    return this.project?.releaseDateDisplayText ?? '';
  }

  get overview() {
    return this.project?.overview ?? '';
  }

  get castMember() {
    const cast = this.project?.castMembers;
    const crew = this.project?.crewMembers;

    if (crew?.length > 0) {
      return crew[0];
    } else if (cast?.length > 0) {
      return cast[0];
    } else {
      return null;
    }
  }

  get titleBadge() {
    if (!this.project?.titleBadges?.length) {
      return null;
    }

    return this.project?.titleBadges[0] || {};
  }

  @Watch('projectSlug', { immediate: true })
  handleProjectSlugChange(
    current: string | undefined,
    previous: string | undefined,
  ) {
    if (current && current !== previous) {
      setTimeout(() => {
        this.showProjectNftPromotion(current);
      }, 1000);
    }
  }
}
